import { Suspense, useContext, useEffect } from 'react';
import { TempAsset } from './Asset';
import { SmartAsset } from './SmartAsset';
import { MachineBoundary } from './MachineBoundary';
import { MachineRenderOrderContext } from './MachineMaterialContext';
import { Entity } from '@repo/frontend-types';
import { TwinEntity } from '@repo/backend-types';

interface Props {
  entity: Entity;
  mapDiv: HTMLDivElement;
  loadingList: Map<string, boolean>;
  lineage: TwinEntity[];
}

/**
 * One machine, which waits and fails on its own. With no model name it is the placeholder. With
 * one, it draws nothing while the model loads (its own `Suspense`, so the rest of the twin draws),
 * and the placeholder if the model fails to load or holds no mesh (its own `MachineBoundary`). A
 * model the existence check reports missing is the placeholder too (`SmartAsset`).
 */
export const HandleAsset = ({ entity, mapDiv, loadingList, lineage }: Props) => {
  // Use actual Asset component if modelFile is specified in metadata, otherwise use placeholder
  const modelFileName = entity.modelFile || '';
  const hasModel = modelFileName.trim() !== '';

  // The scene's loading spinner waits on every key in the loading list. The model is registered
  // here, outside the `Suspense`, because a render that suspends never commits its effects; and a
  // model that fails counts as settled, like a missing one, or the spinner would never clear.
  useEffect(() => {
    if (hasModel && !loadingList.has(modelFileName)) {
      loadingList.set(modelFileName, false);
    }
  }, [hasModel, modelFileName, loadingList]);
  // The placeholder's wrapper is a group too, so it carries the order a 3D plan gives (R2.5).
  const renderOrder = useContext(MachineRenderOrderContext);

  if (hasModel) {
    return (
      <MachineBoundary
        resetKey={modelFileName}
        label={{ bID: entity.bID, name: entity.name, modelFile: modelFileName }}
        onFallback={() => loadingList.set(modelFileName, true)}
        fallback={<TempAsset entity={entity} mapDiv={mapDiv} lineage={lineage} />}
      >
        <Suspense fallback={null}>
          <SmartAsset
            entity={entity}
            mapDiv={mapDiv}
            loadingList={loadingList}
            modelFileName={modelFileName}
            lineage={lineage}
          />
        </Suspense>
      </MachineBoundary>
    );
  }

  return (
    <group renderOrder={renderOrder}>
      <TempAsset entity={entity} mapDiv={mapDiv} lineage={lineage} />
    </group>
  );
};
